<template>
  <v-content :class="isDark ? 'dark-mode' : ''">
    <v-container grid-list-xl style="padding: 0px; top: -40px">
      <!-- Toolbar -->
      <v-card dark class="card-animate-2">
        <v-app-bar color="rgba(12, 22, 27, 0.9)" max-height="75" height="75" elevation="3" dense dark>
          <v-toolbar-title class="d-flex align-center">
            <v-avatar size="40">
              <img :class="{ 'avatar-listening': isListening }" src="@/assets/VoiceNotesIcon.png" />
            </v-avatar>
            &nbsp; Artist Booking Finder
          </v-toolbar-title>

          <v-spacer></v-spacer>

          <!-- <v-btn icon @click="pasteFromClipboard" title="Paste from Clipboard">
            <v-icon color="light-blue lighten-3">mdi-clipboard-arrow-down</v-icon>
          </v-btn> -->

          <v-btn icon @click="toggleDarkMode">
            <v-icon color="cyan lighten-2">{{ isDark ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
          </v-btn>

          <v-progress-circular v-if="loading" indeterminate color="cyan" size="24" width="3" class="ml-4" />
        </v-app-bar>
      </v-card>
<br />
      <!-- Input & Actions -->
      <v-layout row wrap class="card-animate-2 pa-5">
        <v-flex xs12 sm8 md8>
          <v-text-field
            v-model="artist"
            label="Enter Artist Name"
            outlined
            dense
            clearable
            color="cyan"
            class="mb-3"
            @keyup.enter="searchArtist"
          >
            <template v-slot:append>
              <v-icon @click="startVoiceInput" color="blue lighten-3" title="Voice Input">mdi-microphone</v-icon>
            </template>
          </v-text-field>
        </v-flex>

        <v-flex xs12 sm4 md4>
          <v-btn color="cyan darken-2" block @click="searchArtist" :disabled="!artist">
            🔍 Search Booking Info
          </v-btn>
        </v-flex>

        <v-flex xs6 sm4 md4>
          <v-btn color="purple darken-2" block :disabled="!iframeUrl" @click="openInNewTab">
            🌐 Open in New Tab
          </v-btn>
        </v-flex>

        <v-flex xs6 sm4 md4>
          <v-btn color="green darken-2" block @click="googleSearch" :disabled="!artist">
            📊 Google Search
          </v-btn>
        </v-flex>

        <v-btn color="grey" dark class="mt-3" @click="showManageModal = true">
          🧰 Manage Saved Artists
        </v-btn>
      </v-layout>

      <!-- Saved Artists -->
      <v-dialog v-model="showManageModal" max-width="600">
        <v-card>
          <v-card-title>Manage Saved Artists</v-card-title>
          <v-card-text>
            <v-list two-line>
              <v-subheader>Recent</v-subheader>
              <v-list-item v-for="(name, index) in recentArtists" :key="'r-' + index">
                <v-list-item-content>
                  <v-text-field v-model="recentArtists[index]" dense />
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn icon @click="removeRecent(index)">
                    <v-icon color="red">mdi-delete</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>

              <v-subheader>Favorites</v-subheader>
              <v-list-item v-for="(name, index) in favoriteArtists" :key="'f-' + index">
                <v-list-item-content>
                  <v-text-field v-model="favoriteArtists[index]" dense />
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn icon @click="removeFavorite(index)">
                    <v-icon color="red">mdi-delete</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text color="green" @click="saveManagedArtists">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-flex xs12 class="mt-4">
        <h4 class="white--text">Recent Artists</h4>
        <v-chip-group column>
          <v-chip
            v-for="(name, index) in recentArtists"
            :key="'recent-' + index"
            color="cyan darken-3"
            class="ma-2 white--text"
          >
            {{ name }}
            <v-btn icon small @click.stop="addToFavorites(name)">
              <v-icon small color="pink">mdi-heart</v-icon>
            </v-btn>
          </v-chip>
        </v-chip-group>

        <h4 class="white--text mt-4">Favorite Artists</h4>
        <v-chip-group column>
          <v-chip
            v-for="(name, index) in favoriteArtists"
            :key="'fav-' + index"
            color="pink darken-3"
            class="ma-2 white--text"
            @click="loadSavedArtist(name, true)"
          >
            ❤️ {{ name }}
          </v-chip>
        </v-chip-group>
      </v-flex>

      <!-- Iframe & Contact Info -->
      <v-card class="mt-6 card-animate-2" style="height: 600px;" v-if="iframeUrl">
        <v-card-title class="headline white--text">🔎 Booking Info for "{{ artist }}"</v-card-title>
        <v-responsive :aspect-ratio="16/9">
          <iframe
            :src="iframeUrl"
            frameborder="0"
            width="1800px"
            height="800"
            ref="iframe"
            class="iframe rounded-lg"
            @load="onIframeLoad"
          />
        </v-responsive>
      </v-card>

      <v-row class="mt-3">
        <v-col cols="6">
          <v-btn color="indigo darken-2" dark block @click="extractContactInfo">🔍 Extract Info</v-btn>
        </v-col>
        <v-col cols="6">
          <v-btn color="deep-purple accent-3" dark block @click="getAISummary">🤖 Generate AI Summary</v-btn>
        </v-col>
      </v-row>

      <v-card v-if="contactInfo" class="mt-5 pa-4 card-animate-2">
        <h3 class="white--text">Extracted Contact Info:</h3>
        <p class="white--text" v-html="contactInfo"></p>
        <v-row>
          <v-col cols="6">
            <v-btn color="red darken-1" dark block @click="exportPDF">📄 Export PDF</v-btn>
          </v-col>
          <v-col cols="6">
            <v-btn color="blue darken-1" dark block @click="sendEmail">✉️ Email Report</v-btn>
          </v-col>
        </v-row>
        <v-btn color="grey darken-2" dark block @click="printReport">🖨️ Print Report</v-btn>
      </v-card>

      <!-- Google CSE -->
      <div class="google-cse-wrapper mt-5">
        <div class="gcse-searchbox" id="search-form"></div>
        <div class="gcse-searchresults"></div>
      </div>

      <!-- Snackbar -->
      <v-snackbar v-model="showSnackbar" color="cyan darken-3" top right>
        {{ snackbarText }}
      </v-snackbar>
    </v-container>
  </v-content>
</template>

<script>
import jsPDF from "jspdf";

export default {
  name: "ArtistBookingInfo",
  data() {
    return {
      artist: "",
      iframeUrl: "",
      contactInfo: "",
      recognition: null,
      isListening: false,
      loading: false,
      isDark: true,
      showSnackbar: false,
      snackbarText: "",
      recentArtists: [],
      favoriteArtists: [],
      showManageModal: false
    };
  },
  mounted() {
    this.loadSavedArtists();
    this.loadGoogleCSE();
    this.pasteFromClipboard(true);
    this.isDark = localStorage.getItem("theme") === "dark";
    document.body.classList.toggle("dark-mode", this.isDark);
  },
  methods: {
    toggleDarkMode() {
      this.isDark = !this.isDark;
      localStorage.setItem("theme", this.isDark ? "dark" : "light");
      document.body.classList.toggle("dark-mode", this.isDark);
    },
getAISummary() {
  const clean = this.contactInfo.replace(/<[^>]*>/g, "");
  const systemPrompt = `You are an Artist Tour Analyst. Your role is to gather and analyze artist tour data and contact information from multiple sources. Based on the extracted contact and search data, generate a clear summary of the artist including a booking contact report and a relationship tree showing relevant connections and importance.`;

  fetch("https://api.openai.com/v1/chat/completions", {
    method: "POST",
    headers: {
      Authorization: `Bearer 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`, // Replace with your actual key
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      model: "gpt-4.1",
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: clean }
      ],
      max_tokens: 5000,
      temperature: 0.3
    })
  })
    .then(res => res.json())
    .then(data => {
      const msg = data?.choices?.[0]?.message?.content;
      alert(msg || "No summary generated");
    });
},
extractContactInfo() {
  try {
    const iframeDoc = this.$refs.iframe?.contentDocument || this.$refs.iframe?.contentWindow?.document;
    const text = iframeDoc?.body?.innerText || "";

    const email = text.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
    const phone = text.match(/\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/);
    const agency = text.match(/(?:Agency|Company|Group):?\s*([^\n]+)/i);
    const website = text.match(/\bhttps?:\/\/[^\s]+/);
    const social = text.match(/(?:@\w+|facebook\.com\/\w+|instagram\.com\/\w+|linkedin\.com\/in\/\w+)/);

    if (email || phone || agency || website || social) {
      this.contactInfo = `
        <strong>Contact Extracted:</strong><br/>
        Email: ${email?.[0] || "N/A"}<br/>
        Phone: ${phone?.[0] || "N/A"}<br/>
        Agency: ${agency?.[1] || "N/A"}<br/>
        Website: ${website?.[0] || "N/A"}<br/>
        Social: ${social?.[0] || "N/A"}
      `;
      this.snackbarText = "✅ Contact Info Extracted from Bing";
    } else {
      this.contactInfo = "";
      this.snackbarText = "⚠️ No contact info found in Bing results.";
    }
    this.showSnackbar = true;
  } catch (error) {
    console.error("Failed to extract from Bing iframe:", error);
    this.contactInfo = "❌ Error extracting contact info.";
    this.showSnackbar = true;
  }
},
     removeRecent(index) {
    this.recentArtists.splice(index, 1);
  },
  removeFavorite(index) {
    this.favoriteArtists.splice(index, 1);
  },
  saveManagedArtists() {
    localStorage.setItem("recentArtists", JSON.stringify(this.recentArtists));
    localStorage.setItem("favoriteArtists", JSON.stringify(this.favoriteArtists));
    this.showManageModal = false;
    this.snackbarText = "Saved artist updates";
    this.showSnackbar = true;
  },
    searchArtist() {
      if (!this.artist) return;

      this.loading = true;
this.iframeUrl = `https://www.bing.com/search?q=${encodeURIComponent(this.artist)}+booking+agent&FORM=HDRSC1`;
this.saveArtist(this.artist);


// Open Bing search for the artist
// window.open(`https://www.bing.com/search?q=${encodeURIComponent(this.artist)}+booking+agent`, "_blank");



      // Fill Google CSE input
      const input = document.getElementById("gsc-i-id1");
      if (input) {
        input.value = this.artist;
        input.dispatchEvent(new KeyboardEvent("keydown", { keyCode: 13 }));
      }

      setTimeout(() => {
        this.testIframeLoad().then(success => {
          this.loading = false;
          if (!success && !this.fallbackUsed) {
            this.fallbackUsed = true;
            this.iframeUrl = `https://bookingagentinfo.com/celebrity/${encodeURIComponent(this.artist.trim())}`;
          }
        });
      }, 3000);
    },
    addToFavorites(name) {
  const favorites = JSON.parse(localStorage.getItem("favoriteArtists") || "[]");
  if (!favorites.includes(name)) {
    favorites.push(name);
    localStorage.setItem("favoriteArtists", JSON.stringify(favorites));
    this.favoriteArtists = favorites;
    this.snackbarText = `Favorited "${name}"`;
    this.showSnackbar = true;
  }
},

    onIframeLoad() {
      this.loading = false;
    },
    openInNewTab() {
      window.open(this.iframeUrl, "_blank");
    },
    googleSearch() {
      const query = encodeURIComponent(this.artist + " booking agent contact");
      window.open(`https://www.google.com/search?q=${query}`, "_blank");
    },
    exportPDF() {
      const doc = new jsPDF();
      doc.setFontSize(14);
      doc.text(`Booking Contact for ${this.artist}`, 10, 20);
      const lines = this.contactInfo.replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]*>/g, "").split("\n");
      lines.forEach((line, index) => {
        doc.text(line, 10, 30 + index * 8);
      });
      doc.save(`${this.artist}-booking-contact.pdf`);
    },
    sendEmail() {
      const mailto = `mailto:?subject=Booking Info for ${this.artist}&body=${this.contactInfo.replace(/<br\s*\/?>/gi, "%0A").replace(/<[^>]*>/g, "")}`;
      window.open(mailto, "_blank");
    },
    printReport() {
      const win = window.open("", "_blank");
      win.document.write(`<pre>${this.contactInfo.replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]*>/g, "")}</pre>`);
      win.document.close();
      win.print();
    },
    async pasteFromClipboard(autoSearch = false) {
      try {
        const text = await navigator.clipboard.readText();
        if (text && text.length < 100) {
          this.artist = text.trim();
          if (autoSearch) {
            setTimeout(this.searchArtist, 1000);
          }
        }
      } catch (err) {
        console.warn("Clipboard read failed", err);
      }
    },
    startVoiceInput() {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        alert("Voice recognition not supported in this browser.");
        return;
      }

      this.recognition = new SpeechRecognition();
      this.recognition.lang = "en-US";
      this.recognition.interimResults = false;
      this.recognition.maxAlternatives = 1;

      this.isListening = true;

      this.recognition.onresult = event => {
        this.artist = event.results[0][0].transcript;
        this.isListening = false;
        this.searchArtist();
      };

      this.recognition.onerror = event => {
        console.error("Voice input error:", event.error);
        this.isListening = false;
      };

      this.recognition.onend = () => {
        this.isListening = false;
      };

      this.recognition.start();
    },
    saveArtist(name) {
      let recents = JSON.parse(localStorage.getItem("recentArtists") || "[]");
      recents = recents.filter(a => a !== name);
      recents.unshift(name);
      localStorage.setItem("recentArtists", JSON.stringify(recents.slice(0, 10)));
      this.recentArtists = recents.slice(0, 10);
    },
    loadSavedArtists() {
      this.recentArtists = JSON.parse(localStorage.getItem("recentArtists") || "[]");
      this.favoriteArtists = JSON.parse(localStorage.getItem("favoriteArtists") || "[]");
    },
    loadSavedArtist(name, favorite = false) {
      this.artist = name;
      this.searchArtist();
      this.snackbarText = `Loaded ${favorite ? "favorite" : "recent"} artist: ${name}`;
      this.showSnackbar = true;
    },
loadGoogleCSE() {
  if (!window.__gcseLoaded) {
    const script = document.createElement("script");
    script.src = "//cse.google.com/cse.js?cx=87a5cd3086b3e4db7";
    script.async = true;
    script.onload = () => {
      window.__gcseLoaded = true;
      console.log("Google CSE loaded");
    };
    document.head.appendChild(script);
  }
}
  },
  testIframeLoad() {
      return new Promise(resolve => {
        const iframe = this.$refs.iframe;
        if (!iframe) return resolve(false);
        try {
          iframe.contentWindow.location.href;
          resolve(true);
        } catch (e) {
          resolve(false);
        }
      });
    },

};
</script>

<style scoped>
iframe {
  border-radius: 8px;
  border: 1px solid #ccc;
  width: 1700px;
  margin-left: 40px;
}
.card-animate-2 {
  background: linear-gradient(145deg, #0e1c2f, #1c2e46);
  box-shadow: 0 0 15px rgba(72, 239, 255, 0.3);
  border-radius: 18px;
}
.v-card-title,
h3,
p {
  color: #ffffff;
}
.google-cse-wrapper {
  border: 2px solid #0e1c2f;
  padding: 16px;
  background: rgba(12, 22, 27, 0.6);
  border-radius: 12px;
}
.avatar-listening {
  animation: pulseGlow 1.5s infinite;
}
@keyframes pulseGlow {
  0%, 100% {
    box-shadow: 0 0 0px rgba(0, 255, 255, 0.6);
  }
  50% {
    box-shadow: 0 0 12px rgba(0, 255, 255, 0.8);
  }
}
.dark-mode {
  background: #0c161b !important;
  color: white !important;
}
@media (max-width: 600px) {
  iframe {
    height: 400px !important;
  }
}
</style>

<style scoped src="../components/css/Style.css">
@import '../components/css/Style.css';
@import url('https://fonts.googleapis.com/css2?family=Coda:wght@400;800&display=swap');
@import "https://cdnjs.cloudflare.com/ajax/libs/highlight.js/9.12.0/styles/default.min.css";
#editor {
  height: 430px;
}
#app {
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-size: 16px;
  text-align: center;
  color: #d4c3c3;
  /*margin-top: 60px;*/
  /* max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  padding-bottom: 3em; */
}
h1, h2 {
  font-weight: normal;
}
ul {
  list-style-type: none;
  padding: 0;
}
li {
  display: inline-block;
  margin: 0 10px;
}
a {
  color: #42b983;
}
</style>
<style scoped>
.gcse-searchresults {
    top: -100px;
  height: 1020px;
  width: 1880px;
  padding: 10px;
}
.bg-content {
    background: #131f25e7;
    padding: 0px;
    overflow: auto;
    -webkit-animation: gradient 25s ease infinite;
    -animation: gradient 25s ease infinite;
  }
    @-webkit-keyframes gradient {
      0% {
        background-position: 0% 50%;
      }
      50% {
        background-position: 100% 50%;
      }
      100% {
        background-position: 0% 50%;
      }
    }
  
    @keyframes gradient {
      0% {
        background-position: 0% 50%;
      }
      50% {
        background-position: 100% 50%;
      }
      100% {
        background-position: 0% 50%;
      }
      height: 150%;
    }
    
    @media only screen and (max-width: 00px) {
     .bg-content {
    background: #0c161bb2;
    padding: 20px;
    overflow: auto;
    -webkit-animation: gradient 25s ease infinite;
    -animation: gradient 25s ease infinite;
  }
    @-webkit-keyframes gradient {
      0% {
        background-position: 0% 50%;
      }
      50% {
        background-position: 100% 50%;
      }
      100% {
        background-position: 0% 50%;
      }
    }
  
    @keyframes gradient {
      0% {
        background-position: 0% 50%;
      }
      50% {
        background-position: 100% 50%;
      }
      100% {
        background-position: 0% 50%;
      }
      height: 150%;
    } 
  }
.space {
  height: 30px;
}
.avatar-name {
  position: relative;
  top: 10px;
  font-weight: 600;
  margin-left: 8px;
  margin-right: 5px;
}
.user-speach {
  position: center;
  font-size: 12px;
  min-width: 0%;
  max-width: 120%;
  bottom: 15px;
  height: 75px;
  float: inherit;
  left: 0px;
  right: 0px;
  border-radius: 20px;
  padding: 15px;
  color: #fff;
  /* transform: translate(-0%, -50%); */
}

@-webkit-keyframes gradient {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

@keyframes gradient {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

@media (min-width: 600px) {
  .user-speach {
    position: absolute;
    font-size: 15px;
    min-width: 0%;
    max-width: 120%;
    bottom: 30px;
    height: 75px;
    float: inherit;
    left: 0.5%;
    border-radius: 20px;
    padding: 10px;
    color: #fff;
    /* transform: translate(-0%, -50%); */
  }

  @-webkit-keyframes gradient {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  @keyframes gradient {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
}

.btn-text {
  cursor: pointer;
  display: inline-block;
  font-size: 8px;
  font-weight: 100;
  line-height: 1.42857;
  margin-bottom: 0;
  padding: 3px 3px;
  text-align: center;
  vertical-align: middle;
}
@media (min-width: 600px) {
  .btn-text {
    cursor: pointer;
    display: inline-block;
    font-size: 11px;
    font-weight: 300;
    line-height: 1.42857;
    margin-bottom: 0;
    padding: 6px 6px;
    text-align: center;
    vertical-align: middle;
  }
}
.search {
    padding: 30px; 
    top: 200px;
}

@media only screen and (max-width: 800px) {
  .search {
    position: relative;
    padding: 30px; 
    top: 400px;
  }
}
.about {
  padding: 0px;
  width: 100%;
}
.db-content-6 {
  background: #304057;
  background-size: 400% 400%;
  padding: 40px;
  min-height: 110vh;
  -webkit-animation: gradient 25s ease infinite;
  -animation: gradient 25s ease infinite;
}
.btn-right {
  display: flex;
  justify-content: flex-end;
  flex-direction: row;
}
.mg-lr14 {
  margin: 0px 14px;
}
.scroll {
  overflow-y: scroll;
  height: 100%;
}
.theme-bg-card {
  background: rgba(48, 64, 87, 0.8);
  outline-color: #42da91;
  color: #fff;
  border: 1px solid transparent;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.5), 0 6px 6px rgba(0, 0, 0, 0.3);
  height: 400px;
}
.theme-bg-card:hover {
  background: rgba(48, 64, 87, 0.8);
  outline-color: #42da91;
  outline-style: solid;
  animation: glow 1800ms ease-out infinite alternate;
  border-color: #42da91;
  box-shadow: 0 0 10px rgba(90, 128, 191, 0.2),
    inset 0 0 10px rgba(90, 128, 191, 0.1), 0 0px 0 #000;
  color: #efe;
  outline: 20px;
}
.theme-bg-card-2 {
  background: rgba(12, 22, 27, 0.0);
  outline-color: #5a80bf;
  color: rgb(255, 255, 255);
  padding: 10px; 
  top:20px
}
@media only screen and (max-width: 600px) {
  .theme-bg-card-2 {
    background: rgba(12, 22, 27, 0.0);
    outline-color: #5a80bf;
    color: rgb(255, 255, 255);
    padding: 10px; 
    top:0px
  }
}
.theme-bg-card-note {
  background: rgba(48, 64, 87, 0.5);
  outline-color: #42da91;
  color: #fff;
  border: 1px solid transparent;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.5), 0 6px 6px rgba(0, 0, 0, 0.3);
  height: 30vh;
  top: -4rem;
  width: 320px;
}

.theme-bg-card-note:hover {
  background: rgba(48, 64, 87, 0.9);
  outline-color: #42da91;
  outline-style: solid;
  animation: glow 1800ms ease-out infinite alternate;
  border-color: #42da91;
  box-shadow: 0 0 10px rgba(90, 128, 191, 0.2),
    inset 0 0 10px rgba(90, 128, 191, 0.1), 0 0px 0 #000;
  color: #efe;
  outline: 20px;
}
.paste-button-2{
  text-decoration: none;
  min-width: 150px;
  justify-content: center;
  padding: 15px;
  margin: 10px;
  font-family: Arial, Helvetica, sans-serif;
  color: #fff;
  border-radius: 60px;
  animation: glow3 1800ms ease-out infinite alternate;
  box-shadow: 0 0 1px rgba(90, 128, 191, 0.2),
    inset 0 0 3px rgba(90, 128, 191, 0.1), 0 2px 0 #000;
  outline: none;
  }
  @keyframes glow3 {
    0% {
      border-color: #11ece5;
      box-shadow: 0 0 3px rgba(90, 128, 191, 0.2),
        inset 0 0 6px rgba(90, 128, 191, 0.4), 0 1px 0 #000;
    }

    100% {
      border-color: #11ece5;
      box-shadow: 0 0 4px rgba(90, 128, 191, 0.8),
        inset 0 0 10px rgba(90, 128, 191, 0.8), 0 2px 0 #000;
    }
  }


/* .theme-bg-card-3:hover {
  background: rgba(48, 64, 87, 0.9);
  outline-color: #42da91;
  outline-style: solid;
  animation: glow 1800ms ease-out infinite alternate;
  border-color: #42da91;
  box-shadow: 0 0 10px rgba(90, 128, 191, 0.2),
    inset 0 0 10px rgba(90, 128, 191, 0.1), 0 0px 0 #000;
  color: #efe;
  outline: 20px;
} */
.theme-bg-card-4 {
  background: rgba(48, 64, 87, 0.85);
  outline-color: #42da91;
  color: #fff;
  border: 1px solid transparent;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.5), 0 6px 6px rgba(0, 0, 0, 0.3);
  height: 10vh;
  border-radius: 30px;
}

.theme-bg-card-5 {
  background: rgba(48, 64, 87, 0.6);
  outline-color: #5a80bf;
  color: #fff;
}


@media only screen and (max-width: 600px) {
.theme-bg-card-5 {
  background: rgba(48, 64, 87, 0.6);
  outline-color: #5a80bf;
  color: #fff;
}
}
.theme-bg-card-6 {
  background: rgba(12, 22, 27, 0.4);
  outline-color: #5a80bf;
  color: #fff;
  height: 200px;
}
.chart-text {
  color: #fff;
  text-align: center;
  font-size: 1.3rem;
  font-weight: normal;
  font-family: 'Courier New', Courier, monospace;
}
.tile-title {
  color: #fff;
  text-align: center;
  font-size: 1.3rem;
  font-weight: normal;
  font-family: Arimo, sans-serif;
}
.chart-title {
  color: #fff;
  text-align: center;
  font-weight: normal;
  font-family: Arimo, sans-serif;
  padding: 10px;
}
@media only screen and (max-width: 600px) {
  .chart-title {
    color: #fff;
    text-align: center;
    font-size: 0.9rem;
    font-weight: bold;
    font-family: Arimo, sans-serif;
  }
}

.chart-title-4 {
  color: #fff;
  text-align: center;
  font-size: 0.4rem;
  font-weight: normal;
  font-family: Arimo, sans-serif;
  padding: 10px;
}
@media only screen and (max-width: 600px) {
  .chart-title-4 {
    color: #fff;
    text-align: center;
    font-size: 0.9rem;
    font-weight: bold;
    font-family: Arimo, sans-serif;
  }
}

.theme-bg-card-5 {
  background: rgba(48, 64, 87, 0.0);
  border-radius: 25px;
  width: 35%;

}


@media only screen and (max-width: 600px) {
.theme-bg-card-5 {
  background: rgba(48, 64, 87, 0.6);

  width: 50%;
}
}
.field {
  border-radius: 20px;
  border-color: rgb(36, 212, 65);
  border-radius: 30px;
  padding-block: 20px;
  border: 1px solid transparent;
}

.field label {
  display: block;
  font-size: 12px;
  color: rgb(255, 255, 255);
}

.field input {
  display: block;
  min-width: 350px;
  line-height: 3.5;
  font-size: 13px;
  color: rgb(36, 212, 65);
  border-radius: 20px;
  padding-block: 15px;
  margin-block: 10px;
  border: 1px solid transparent;
}

input[type="submit"] {
  display: block;
  padding: 20px 40px;
  font-size: 14px;
  background-color: rgb(36, 212, 65);
  color: #fff;
  border: none;
  background: rgb(36, 212, 65, 0.4);
  outline-color: #42da91;
  color: #fff;
  border: 1px solid transparent;
  height: 80px;
  margin-block: 5px;
}


.chart-title-2 {
  text-align: center;
  font-size: 1.3rem;
  font-weight: 500;
  color: #42da91;
  font-family: Arial, Helvetica, sans-serif;
  padding: 20px;
}
@media only screen and (max-width: 600px) {
  .chart-title-2 {
    text-align: center;
    font-size: 12px;
    font-weight: 500;
    color: #42da91;
    font-family: Arial, Helvetica, sans-serif;
    padding: 5px;
  }
}

/* 
.button {
  border-radius: 30px;
  padding: 8px;
  width: 3px;
  border: outline;
  background: #304057;
  opacity: 0.9;
  cursor: pointer;
  margin-left: auto;
  margin-right: auto;
  align: center;
  outline-color: #5a80bf;
  outline-style: solid;
  animation: glow 1800ms ease-out infinite alternate;
  border-color: #5a80bf;
  box-shadow: 0 0 5px rgba(90, 128, 191, 0.2),
    inset 0 0 3px rgba(90, 128, 191, 0.1), 0 2px 0 #000;
  outline: none;
  }
  @keyframes glow {
    0% {
      border-color: #11ece5;
      box-shadow: 0 0 5px rgba(90, 128, 191, 0.2),
        inset 0 0 6px rgba(90, 128, 191, 0.4), 0 2px 0 #000;
    }
    100% {
      border-color: #11ece5;
      box-shadow: 0 0 8px rgba(90, 128, 191, 0.8),
        inset 0 0 10px rgba(90, 128, 191, 0.8), 0 2px 0 #000;
    }
  }
@media only screen and (max-width: 600px) {
  
.button {
  border-radius: 40px;
  padding: 5px;
  width: 5px;
  border: outline;
  background: #304057;
  opacity: 0.9;
  cursor: pointer;
  margin-left: auto;
  margin-right: auto;
  align: center;
  outline-color: #5a80bf;
  outline-style: solid;
  animation: glow 1800ms ease-out infinite alternate;
  border-color: #5a80bf;
  box-shadow: 0 0 5px rgba(90, 128, 191, 0.2),
    inset 0 0 3px rgba(90, 128, 191, 0.1), 0 2px 0 #000;
  outline: none;
  }
  @keyframes glow {
    0% {
      border-color: #11ece5;
      box-shadow: 0 0 5px rgba(90, 128, 191, 0.2),
        inset 0 0 6px rgba(90, 128, 191, 0.4), 0 2px 0 #000;
    }
    100% {
      border-color: #11ece5;
      box-shadow: 0 0 8px rgba(90, 128, 191, 0.8),
        inset 0 0 10px rgba(90, 128, 191, 0.8), 0 2px 0 #000;
    }
  }
} */

.button2  {
  border-radius: 30px;
  padding:  0px;
  border: outline;
  background: #304057;
  opacity: 0.9;
  cursor: pointer;
  margin-left: auto;
  margin-right: auto;
  outline-color: #5a80bf;
  outline-style: solid;
  animation: glow 1800ms ease-out infinite alternate;
  border-color: #5a80bf;
  box-shadow: 0 0 5px rgba(90, 128, 191, 0.2),
    inset 0 0 3px rgba(90, 128, 191, 0.1), 0 2px 0 #000;
  outline: none;
  }
  @keyframes glow {
    0% {
      border-color: #11ece5;
      box-shadow: 0 0 5px rgba(90, 128, 191, 0.2),
        inset 0 0 6px rgba(90, 128, 191, 0.4), 0 2px 0 #000;
    }
    100% {
      border-color: #11ece5;
      box-shadow: 0 0 8px rgba(90, 128, 191, 0.8),
        inset 0 0 10px rgba(90, 128, 191, 0.8), 0 2px 0 #000;
    }
  }

.chart-content {
  opacity: 0.7;
  text-align: center;
  margin-left: 0%;
  top: -75px;
  background: rgba(12, 22, 27, 0.4);
  outline-color: rgba(90, 128, 191, 0.5);
  outline-style: solid;
  animation: glow 1800ms ease-out infinite alternate;
  border-color: #5a80bf;
  box-shadow: 0 0 5px rgba(120, 204, 226, 0.4), inset 0 0 10px rgba(120, 204, 226, 0.2), 0 4px 0 #000;
  color: #efe;
  outline-width: 2px;
  padding: 15px;
  border-radius: 10px;
  margin: 10px;
}

@-webkit-keyframes gradient {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

@keyframes gradient {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
  height: 150%;
}


.thrash {
  font-size: 20px;
  cursor: pointer;
}

.card-animate-2 {
  animation-name: slideExpandUp;
  /* position: relative;
  position-fromtop: 100px; */
  animation-duration: 3s;
  transition: opacity 1s Elastic.easeIn;
}

.slideExpandUp {
  animation-name: slideExpandUp;
  -webkit-animation-name: slideExpandUp;

  animation-duration: 2.7s;
  -webkit-animation-duration: 2.7s;

  animation-timing-function: ease-out;
  -webkit-animation-timing-function: ease -out;

  visibility: visible !important;
}

@keyframes slideExpandUp {
  0% {
    -webkit-transform: translateY(10%) scaleX(0.94);
  }
  30% {
    -webkit-transform: translateY(-5%) scaleX(0.95);
  }
  40% {
    -webkit-transform: translateY(3%) scaleX(0.96);
  }
  50% {
    -webkit-transform: translateY(0%) scaleX(1);
  }
}

@-webkit-keyframes slideExpandUp {
  0% {
    -webkit-transform: translateY(10%) scaleX(0.94);
  }
  30% {
    -webkit-transform: translateY(-5%) scaleX(0.95);
  }
  40% {
    -webkit-transform: translateY(3%) scaleX(0.96);
  }
  50% {
    -webkit-transform: translateY(0%) scaleX(1);
  }
}
.space {
  height: 20px;
}

.card-animate {
  animation-name: slideExpandDown;
  /* position: relative;
  position-fromtop: 100px; */
  animation-duration: 3s;
  transition: opacity 1s Elastic.easeIn;
}
.slideExpandDown {
  animation-name: slideExpandDown;
  -webkit-animation-name: slideExpandDown;
  animation-duration: 2.7s;
  -webkit-animation-duration: 2.7s;
  animation-timing-function: ease-out;
  -webkit-animation-timing-function: ease -out;
  visibility: visible !important;
}
@keyframes slideExpandDown {
  0% {
    transform: translateY(100%) scaleX(0.94);
  }
  30% {
    transform: translateY(-4%) scaleX(0.95);
  }
  40% {
    transform: translateY(2%) scaleX(0.96);
  }
  50% {
    transform: translateY(0%) scaleX(1);
  }
}
@-webkit-keyframes slideExpandDown {
  0% {
    -webkit-transform: translateY(100%) scaleX(0.94);
  }
  30% {
    -webkit-transform: translateY(-4%) scaleX(0.95);
  }
  40% {
    -webkit-transform: translateY(2%) scaleX(0.96);
  }
  50% {
    -webkit-transform: translateY(0%) scaleX(1);
  }
}
.popup-container {
  max-height: 90vh;
  width: 90vw;
  background: rgba(48, 64, 87, 0.6);
  padding: 10px 40px;
}
.font-weight-thin {
  margin-top: 2rem;
  margin-bottom: 2rem;
  font-weight: 600;
}
.d-flex-row {
  display: flex;
  flex-direction: row;
}
.d-flex-col {
  display: flex;
  flex-direction: column;
}
.date-picker {
  flex: 1 1 0%;
  padding: 17px;
  border: 1px solid #fff;
  height: 53px;
  width: auto;
  color: #fff;
  margin: 0 14px;
  border-radius: 5px;
}
.form-control {
  border: 1px solid #5e6570;
  padding: 20px 20px;
  margin-block: 3px 5px;
  border-radius: 5px;
  font-size: 14px;
  background: transparent;
  color: rgb(36, 212, 65);
}
@media only screen and (max-width: 600px) {
 .form-control {
  border: 1px solid #5e6570;
  padding: 20px 20px;
  margin-block: 3px 5px;
  border-radius: 10px;
  font-size: 12px;
  background: transparent;
  color: rgb(36, 212, 65);
 }
}
.form-control-2 {
  border: 1px solid #5e6570;
  padding: 10px 10px;
  border-radius: 5px;
  font-size: 14px;
  background: transparent;
  color: rgb(255, 255, 255);
  
}
@media only screen and (max-width: 600px) {
 .form-control-2 {
  border: 1px solid #5e6570;
  padding: 20px 20px;
  margin-block: 3px 5px;
  border-radius: 10px;
  font-size: 12px;
  background: transparent;
  color: rgb(36, 212, 65);
 }
}
.form-control-3 {
  display: block;
  min-width: 480px;
  line-height: 2.5;
  font-size: 18px;
  color: rgb(36, 212, 65);
  border-radius: 20px;
  padding-block: 20px;
  margin-block: 5px;
  border: 1px solid transparent;
}
@media only screen and (max-width: 600px) {
 .form-control-3 {
  display: block;
  min-width: 480px;
  line-height: 2.5;
  font-size: 16px;
  color: rgb(36, 212, 65);
  border-radius: 20px;
  padding-block: 20px;
  margin-block: 5px;
  border: 1px solid transparent;
 }
}
.space2 {
  height: 50px;
}
.action-btn {
  font-size: 20px;
  cursor: pointer;
  position: relative;
  top: 5px;
}
.typing {
  padding: 15px;
  /* background: rgba(37, 50, 68, 0.6);
    border-radius: 15px; */
  border-radius: 20px;
  min-width: 0%;
  margin-left: 0px;
  font-family: Arimo, sans-serif;
  font-weight: normal;
  font-size: 1.26em;
  background: rgba(37, 50, 68, 0.2);
  -webkit-animation: titleAnimation 20s infinite ease;
  -animation: titleAnimation 100s normal forwards ease-in-out;
}

@keyframes titleAnimation {
  0% {
    transform: translateY(-48px);
    opacity: 0;
    -webkit-clip-path: polygon(100% 0, 100% 100%, 0 100%, 0 80%);
    clip-path: polygon(100% 0, 100% 100%, 0 100%, 0 80%);
  }
  20% {
    transform: translateY(0);
    opacity: 1;
    -webkit-clip-path: polygon(100% 0, 100% 100%, 0 100%, 0 0%);
    clip-path: polygon(100% 0, 100% 100%, 0 100%, 0 0%);
  }
  80% {
    transform: translateY(0);
    opacity: 1;
    -webkit-clip-path: polygon(100% 0, 100% 100%, 0 100%, 0 0%);
    clip-path: polygon(100% 0, 100% 100%, 0 100%, 0 0%);
  }
  100% {
    transform: translateY(50px);
    opacity: 0;
    -webkit-clip-path: polygon(100% 0, 100% -0%, 0 100%, 0 100%);
    clip-path: polygon(100% 0, 100% -0%, 0 100%, 0 100%);
  }
}
.typing2 {
  padding: 15px;
  left: 0px;
  right: 0px;
  /* background: rgba(37, 50, 68, 0.6);
    border-radius: 15px; */
  border-radius: 20px;
  min-width: 0%;
  max-width: 90%;
  margin-left: 60px;
  font-family: Arimo, sans-serif;
  font-weight: normal;
  font-size: 1.26em;
}

.product-list {
  position: absolute;
  margin-left: 160px;
  margin-top: 38px;
  width: 50%;
  padding: 0px;
  list-style: none;
  background: rgba(48, 64, 87, 0.3);
  color: rgb(255, 255, 255);
  line-height: 2;
  max-height: 200px;
  overflow: auto;
  border: 1px solid #7c7c7c;
  border-radius: 8px;
}
.item {
  padding: 10px;
  border-bottom: 1px solid #ccc;
  cursor: pointer;
}
.item:hover {
  background: rgba(48, 64, 87, 0.8);
}
.calc {
  width: 200px;
  margin-bottom: 20px;
}
.loader {
  border: 3px solid #f3f3f3;
  border-radius: 50%;
  border-top: 3px solid #3498db;
  width: 15px;
  height: 15px;
  -webkit-animation: spin 2s linear infinite; /* Safari */
  animation: spin 2s linear infinite;
  position: relative;
  margin-top: 12px;
  margin-left: -15px;
}
/* Safari */
@-webkit-keyframes spin {
  0% {
    -webkit-transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
  }
}
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>


<style lang="scss">
$base-duration: 500ms;

// Colors
$color-1: rgba(48, 64, 87, 0.85);
$color-2: rgba(48, 64, 87, 0.2);
$color-3: #2ecc71;

// Breakpoints
$sm: new-breakpoint(min-width 320px);
$med: new-breakpoint(min-width 768px);
$lg: new-breakpoint(min-width 1024px);

*, *:before, *:after {
	box-sizing: border-box;
	outline: none;
}

.hide { display: none;
}

.button-4 {
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 100%;
	margin: 0px;  
	padding: 1.5rem 3.125rem;
	background-color: $color-2;
	border: none;
  border-radius: 0.5rem;
	box-shadow:
		0 -1.25rem 1rem -1.5rem rgba(black, 0.4),
		0 0.5rem 1.75rem -0.25rem rgba(black, 0.3),
		0 1.25rem 2.5rem rgba(black, 0.2);
	font-weight: 300;
	transition: $base-duration ease;
	overflow: hidden; 
	
	&:before {
		position: absolute;
		content: '';
		bottom: 0;
		left: 0;
		width: 0%;
		height: 100%;
		background-color: lighten($color-3,10%);
	}
	
	span {
		position: absolute;
		line-height: 0;
		
		i {
			transform-origin: center center;
		}

		&:nth-of-type(1) {
			top: 50%;
			transform: translateY(-50%);
		}
		
		&:nth-of-type(2) {
			top: 100%;
			transform: translateY(0%);
			font-size: 24px;
		}
		
		&:nth-of-type(3) {
			display: none;
		}
	}
}
.active {
	background-color: $color-3;
	
	&:before {
		width: 100%;
		transition: width 3s linear;
	}

	span {
		
		&:nth-of-type(1) {
			top: -100%;
			transform: translateY(-50%); 
		}

		&:nth-of-type(2) {
			top: 50%;
			transform: translateY(-50%);

			i {
				animation: loading $base-duration linear infinite;
			}
		}
		
		&:nth-of-type(3) {
			display: none;
		}
	}
}

.finished {
	background-color: lighten($color-3,10%);
	
	.submit {
		display: none; 
	}
	
	.loading {
		display: none;
	}
	
	.check {
		display: block !important;
		font-size: 24px;
		animation: scale 0.5s linear;
		
		i {
			transform-origin: center center;
		}
	}
	
}

@keyframes loading {
	100% { transform: rotate(360deg); }
}

@keyframes scale {
	0%   { transform: scale(10); }
	50%  { transform: scale(0.2); }
	70%  { transform: scale(1.2); }
	90%  { transform: scale(0.7); }
	100% { transform: scale(1); }
}

@media only screen and (max-width: 400px) {
   .button-4 {
	position: absolute;
	width: 120px;
  height: 250px;
	margin: 0px;  
	padding: 0.5rem 0.5rem;
	background-color: $color-2;
	border: none;
	border-radius: 25px;
  top: -180px; 
  left: 5px;

	// box-shadow:
	// 	0 -1.25rem 1rem -1.5rem rgba(black, 0.4),
	// 	0 0.5rem 1.75rem -0.25rem rgba(black, 0.3),
	// 	0 1.25rem 2.5rem rgba(black, 0.2);
	font-weight: 300;
	text-transform: uppercase;
	transition: $base-duration ease;
	overflow: hidden; 
	
	&:before {
		position: absolute;
		content: '';
		bottom: 0;
		left: 0;
		width: 0%;
		height: 100%;
		background-color: lighten($color-3,10%);
	}
	
	span {
		position: absolute;
		line-height: 0;
		
		i {
			transform-origin: center center;
		}

		&:nth-of-type(1) {
			top: 50%;
			transform: translateY(-50%);
		}
		
		&:nth-of-type(2) {
			top: 100%;
			transform: translateY(0%);
			font-size: 24px;
		}
		
		&:nth-of-type(3) {
			display: none;
		}
	}
}

.active {
	background-color: $color-3;
	
	&:before {
		width: 100%;
		transition: width 3s linear;
	}

	span {
		
		&:nth-of-type(1) {
			top: -100%;
			transform: translateY(-50%); 
		}

		&:nth-of-type(2) {
			top: 50%;
			transform: translateY(-50%);

			i {
				animation: loading $base-duration linear infinite;
			}
		}
		
		&:nth-of-type(3) {
			display: none;
		}
	}
}

.finished {
	background-color: lighten($color-3,10%);
	
	.submit {
		display: none; 
	}
	
	.loading {
		display: none;
	}
	
	.check {
		display: block !important;
		font-size: 24px;
		animation: scale 0.5s linear;
		
		i {
			transform-origin: center center;
		}
	}
	
}

@keyframes loading {
	100% { transform: rotate(360deg); }
}

@keyframes scale {
	0%   { transform: scale(10); }
	50%  { transform: scale(0.2); }
	70%  { transform: scale(1.2); }
	90%  { transform: scale(0.7); }
	100% { transform: scale(1); }
}
}
</style>